import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ProductService from '../services/product.service';
import WishlistService from '../services/wishlist.service';
import ReviewService from '../services/review.service';
import { useCart } from '../context/CartContext';
import { ArrowLeft, ShoppingBag, Truck, ShieldCheck, Heart, Minus, Plus, Box } from 'lucide-react';
import toast from 'react-hot-toast';
import { Button } from '../components/ui/Button';
import { formatPrice } from '../utils/currency';
import '@google/model-viewer';

const ProductDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { addToCart } = useCart();
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [quantity, setQuantity] = useState(1);
    const [selectedImage, setSelectedImage] = useState('');
    const [selectedVariant, setSelectedVariant] = useState(null);
    const [show3D, setShow3D] = useState(false);

    const [isInWishlist, setIsInWishlist] = useState(false);
    const [wishlistLoading, setWishlistLoading] = useState(false);
    const [reviews, setReviews] = useState([]);
    const [reviewStats, setReviewStats] = useState({ avg: 0, count: 0 });

    useEffect(() => {
        fetchProduct();
    }, [id]);

    const fetchProduct = async () => {
        try {
            const response = await ProductService.getProduct(id);
            setProduct(response.data);
            const images = response.data.images || [];
            const primary = images.find(img => img.is_primary) || images[0];
            if (primary) {
                setSelectedImage(primary.image_url || primary.url);
            } else if (response.data.primary_image) {
                setSelectedImage(response.data.primary_image);
            }

            if (response.data.variants && response.data.variants.length > 0) {
                setSelectedVariant(response.data.variants[0]);
            }
        } catch (error) {
            console.error(error);
            toast.error('Failed to load product');
        } finally {
            setLoading(false);
        }
    };

    const handleAddToCart = async (buyNow = false) => {
        const variantId = selectedVariant?.id || product?.variants?.[0]?.id;
        if (variantId) {
            await addToCart(variantId, quantity);
            if (buyNow) navigate('/checkout');
        } else {
            toast.error('Product variant not available');
        }
    };

    const checkWishlistStatus = async () => {
        try {
            const wishlist = await WishlistService.getWishlist();
            if (wishlist && wishlist.length > 0) {
                const defaultList = wishlist.find(w => w.is_default) || wishlist[0];
                const found = defaultList.items.some(item => item.product === product.id);
                setIsInWishlist(found);
            }
        } catch (err) {
            console.error(err);
        }
    };

    const fetchReviews = async () => {
        try {
            const data = await ReviewService.getProductReviews(product.id);
            setReviews(data);
            if (data.length > 0) {
                const total = data.reduce((acc, r) => acc + r.rating, 0);
                setReviewStats({ avg: (total / data.length).toFixed(1), count: data.length });
            }
        } catch (err) {
            console.error(err);
        }
    };

    const toggleWishlist = async () => {
        setWishlistLoading(true);
        try {
            if (isInWishlist) {
                const wishlistList = await WishlistService.getWishlist();
                const defaultList = wishlistList.find(w => w.is_default) || wishlistList[0];
                if (defaultList) {
                    await WishlistService.removeFromWishlist(defaultList.id, product.id);
                    setIsInWishlist(false);
                    toast.success("Removed from wishlist");
                }
            } else {
                await WishlistService.addToWishlist({ product_id: product.id });
                setIsInWishlist(true);
                toast.success("Added to wishlist");
            }
        } catch (err) {
            console.error(err);
            toast.error("Failed to update wishlist");
        } finally {
            setWishlistLoading(false);
        }
    };

    useEffect(() => {
        if (product) {
            checkWishlistStatus();
            fetchReviews();
            if (product.model_3d_url) {
                setShow3D(true);
            }
        }
    }, [product]);

    if (loading) return <div className="min-h-screen flex items-center justify-center text-sm uppercase tracking-widest text-gray-400">Loading...</div>;
    if (!product) return <div className="min-h-screen flex items-center justify-center text-sm uppercase tracking-widest text-gray-400">Product not found</div>;

    const images = product.images || [];

    return (
        <div className="max-w-7xl mx-auto px-6 py-12 md:py-20">
            <button
                onClick={() => navigate(-1)}
                className="flex items-center gap-2 text-xs uppercase tracking-widest text-gray-400 hover:text-black mb-12 transition-colors"
            >
                <ArrowLeft size={16} /> Back
            </button>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">

                {/* Image Section */}
                <div className="lg:col-span-7 flex flex-col gap-6">
                    <div className="bg-gray-50 aspect-[4/5] w-full flex items-center justify-center relative overflow-hidden group">
                        {product.model_3d_url && show3D ? (
                            <model-viewer
                                src={product.model_3d_url}
                                ar
                                auto-rotate
                                camera-controls
                                style={{ width: '100%', height: '100%' }}
                            ></model-viewer>
                        ) : selectedImage ? (
                            <img src={selectedImage} alt={product.name} className="w-full h-full object-cover" />
                        ) : (
                            <div className="text-gray-300 font-bold text-2xl">No Image</div>
                        )}

                        {product.model_3d_url && (
                            <button
                                onClick={() => setShow3D(!show3D)}
                                className="absolute bottom-6 right-6 bg-black text-white px-4 py-2 rounded-full text-xs uppercase tracking-widest font-bold shadow-lg hover:bg-gray-800 transition-colors z-20 flex items-center gap-2"
                            >
                                <Box size={16} /> {show3D ? 'View 2D' : 'View 3D'}
                            </button>
                        )}

                        <button
                            onClick={toggleWishlist}
                            disabled={wishlistLoading}
                            className={`absolute top-6 right-6 w-12 h-12 rounded-full flex items-center justify-center shadow-sm transition-all z-20 ${isInWishlist ? 'bg-red-500 text-white' : 'bg-white text-black hover:text-red-500'}`}
                        >
                            <Heart size={20} fill={isInWishlist ? "currentColor" : "none"} />
                        </button>
                    </div>

                    {/* Thumbnail Gallery */}
                    {images.length > 0 && (
                        <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide">
                            {images.map((img, idx) => {
                                const url = img.image_url || img.url;
                                const isSelected = selectedImage === url;
                                return (
                                    <button
                                        key={img.id || idx}
                                        onClick={() => setSelectedImage(url)}
                                        className={`flex-shrink-0 w-20 h-24 bg-gray-50 overflow-hidden transition-all ${isSelected ? 'ring-2 ring-black' : 'opacity-70 hover:opacity-100'}`}
                                    >
                                        <img src={url} alt="" className="w-full h-full object-cover" />
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Info Section */}
                <div className="lg:col-span-5 flex flex-col pt-4">
                    <div className="flex items-center gap-4 mb-6">
                        {product.is_new && <span className="px-2 py-1 bg-black text-white text-[10px] font-bold uppercase tracking-widest">New Arrival</span>}
                        <div className="text-xs font-medium text-gray-500 uppercase tracking-widest font-sans">
                            {reviewStats.count > 0 ? `${reviewStats.avg} / 5.0` : <span className="font-sans">No reviews</span>}
                        </div>
                    </div>

                    <h1 className="font-serif text-4xl md:text-5xl text-black mb-6 leading-tight">{product.name}</h1>
                    <p className="font-light text-gray-600 mb-8 leading-relaxed">{product.description || "No description available."}</p>

                    <div className="text-3xl font-medium font-sans mb-10">
                        {selectedVariant ? formatPrice(selectedVariant.price) : formatPrice(product.base_price)}
                    </div>

                    {/* Actions */}
                    <div className="space-y-6 mb-12 border-t border-b border-gray-100 py-8">
                        {/* Quantity */}
                        <div className="flex items-center justify-between">
                            <span className="text-sm uppercase tracking-widest font-bold">Quantity</span>
                            <div className="flex items-center gap-4 border border-gray-200 rounded-full px-4 py-2">
                                <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="hover:text-gray-500"><Minus size={16} /></button>
                                <span className="font-sans w-4 text-center">{quantity}</span>
                                <button onClick={() => setQuantity(quantity + 1)} className="hover:text-gray-500"><Plus size={16} /></button>
                            </div>
                        </div>

                        {/* Buttons */}
                        <div className="grid grid-cols-2 gap-4">
                            <Button variant="secondary" onClick={() => handleAddToCart(false)}>
                                Add to Bag
                            </Button>
                            <Button onClick={() => handleAddToCart(true)}>
                                Buy Now
                            </Button>
                        </div>
                    </div>

                    {/* Value Props */}
                    <div className="space-y-4">
                        <div className="flex items-center gap-4">
                            <Truck size={20} className="text-gray-400" />
                            <div className="text-sm">
                                <span className="font-bold block text-xs uppercase tracking-widest">Free Shipping</span>
                                <span className="text-gray-500">On all orders over {formatPrice(500)}</span>
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            <ShieldCheck size={20} className="text-gray-400" />
                            <div className="text-sm">
                                <span className="font-bold block text-xs uppercase tracking-widest">Secure Checkout</span>
                                <span className="text-gray-500">Encrypted payment processing</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Reviews Section (Minimal) */}
            <div className="mt-24 max-w-3xl">
                <h2 className="font-serif text-2xl mb-8">Customer Reviews</h2>
                {reviews.length > 0 ? (
                    <div className="space-y-8 divide-y divide-gray-100">
                        {reviews.map((review) => (
                            <div key={review.id} className="pt-8 first:pt-0">
                                <div className="flex items-center justify-between mb-2">
                                    <h4 className="font-bold text-sm">{review.title}</h4>
                                    <span className="text-xs text-gray-400">{new Date(review.created_at).toLocaleDateString()}</span>
                                </div>
                                <div className="text-yellow-500 text-xs mb-2">{'★'.repeat(review.rating)}{'☆'.repeat(5 - review.rating)}</div>
                                <p className="text-gray-600 font-light text-sm">{review.comment}</p>
                                <p className="text-xs text-gray-400 mt-2">— {review.user_name || 'Anonymous'}</p>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="text-gray-400 text-sm italic">No reviews yet.</p>
                )}
            </div>
        </div>
    );
};

export default ProductDetails;
